// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  FIRST_TOUCH_SORT_COLUMNS,
  formatAcquisitionDate,
  formatEntityTypeLabel,
} from '../../utils/firstTouchReport';

const SORTABLE_COLUMNS = [
  { key: FIRST_TOUCH_SORT_COLUMNS.name, label: 'Name', align: 'left' },
  { key: FIRST_TOUCH_SORT_COLUMNS.entityType, label: 'Type', align: 'left' },
  { key: FIRST_TOUCH_SORT_COLUMNS.city, label: 'City', align: 'left' },
  { key: FIRST_TOUCH_SORT_COLUMNS.state, label: 'State', align: 'left' },
  { key: FIRST_TOUCH_SORT_COLUMNS.userCount, label: 'Users', align: 'right' },
  {
    key: FIRST_TOUCH_SORT_COLUMNS.earliestAcquisition,
    label: 'Earliest (UTC)',
    align: 'left',
  },
  {
    key: FIRST_TOUCH_SORT_COLUMNS.latestAcquisition,
    label: 'Latest (UTC)',
    align: 'left',
  },
];

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {string} sortKey
 * @param {string} sortDirection
 * @returns {Array<Record<string, unknown>>}
 */
function sortTableRows(rows, sortKey, sortDirection) {
  const sortedRows = [...rows];
  sortedRows.sort((leftRow, rightRow) => {
    const leftValue = leftRow[sortKey];
    const rightValue = rightRow[sortKey];
    if (typeof leftValue === 'string' && typeof rightValue === 'string') {
      const comparison = leftValue.localeCompare(rightValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    const leftNumber = typeof leftValue === 'number' ? leftValue : 0;
    const rightNumber = typeof rightValue === 'number' ? rightValue : 0;
    const comparison = leftNumber - rightNumber;
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  return sortedRows;
}

/**
 * @param {Object} props
 * @param {string} props.sortKey
 * @param {string} props.sortDirection
 * @param {Function} props.onSortLabelClick
 */
function FirstTouchHotspotsTableHead({ sortKey, sortDirection, onSortLabelClick }) {
  return (
    <TableHead>
      <TableRow>
        {SORTABLE_COLUMNS.map((column) => (
          <TableCell
            key={column.key}
            align={column.align}
            sortDirection={sortKey === column.key ? sortDirection : false}
          >
            <TableSortLabel
              active={sortKey === column.key}
              direction={sortKey === column.key ? sortDirection : 'asc'}
              onClick={onSortLabelClick}
              data-sort-key={column.key}
            >
              {column.label}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell>Map</TableCell>
      </TableRow>
    </TableHead>
  );
}

FirstTouchHotspotsTableHead.propTypes = {
  sortKey: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  onSortLabelClick: PropTypes.func.isRequired,
};

function FirstTouchHotspotsTable({ rows }) {
  const [sortKey, setSortKey] = useState(FIRST_TOUCH_SORT_COLUMNS.userCount);
  const [sortDirection, setSortDirection] = useState('desc');

  const sortedRows = useMemo(
    () => sortTableRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection],
  );

  const handleSort = useCallback((columnKey) => {
    if (sortKey === columnKey) {
      setSortDirection((currentDirection) =>
        currentDirection === 'asc' ? 'desc' : 'asc',
      );
      return;
    }
    setSortKey(columnKey);
    setSortDirection(columnKey === FIRST_TOUCH_SORT_COLUMNS.name ? 'asc' : 'desc');
  }, [sortKey]);

  const handleSortLabelClick = useCallback((event) => {
    const columnKey = event.currentTarget.getAttribute('data-sort-key');
    if (columnKey) {
      handleSort(columnKey);
    }
  }, [handleSort]);

  return (
    <TableContainer>
      <Table size="small" aria-label="First-touch hotspots">
        <FirstTouchHotspotsTableHead
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSortLabelClick={handleSortLabelClick}
        />
        <TableBody>
          {sortedRows.map((row) => (
            <TableRow key={`${row.entityType}-${row.entityId}`} hover>
              <TableCell>{row.name}</TableCell>
              <TableCell>{formatEntityTypeLabel(row.entityType)}</TableCell>
              <TableCell>{row.city || '—'}</TableCell>
              <TableCell>{row.state || '—'}</TableCell>
              <TableCell align="right">{row.userCount}</TableCell>
              <TableCell>{formatAcquisitionDate(row.earliestAcquisition)}</TableCell>
              <TableCell>{formatAcquisitionDate(row.latestAcquisition)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.hasCoordinates ? 'On map' : 'No coordinates'}
                  color={row.hasCoordinates ? 'success' : 'default'}
                  variant="outlined"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

FirstTouchHotspotsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default FirstTouchHotspotsTable;
